(function () {
  const PACK_MAXIMUM = 3;

  function getProductInfo(selector) {
    return selector.closest('product-info');
  }

  function getProductForm(selector) {
    return getProductInfo(selector)?.querySelector('form[action*="/cart/add"], form[action$="/cart/add"]');
  }

  function getSelectedPack(selector) {
    return selector.querySelector('[data-pack-quantity].is-selected');
  }

  function getSelectedQuantity(selector) {
    return Number(getSelectedPack(selector)?.dataset.packQuantity || 1);
  }

  function getVariants(selector) {
    if (selector.packVariants) return selector.packVariants;

    try {
      selector.packVariants = JSON.parse(selector.querySelector('[data-pack-variants]')?.textContent || '[]');
    } catch (error) {
      console.error('Could not read pack variants', error);
      selector.packVariants = [];
    }

    return selector.packVariants;
  }

  function getNativeVariant(selector) {
    const variantId = Number(getProductForm(selector)?.querySelector('input[name="id"]')?.value);
    const variants = getVariants(selector);
    return variants.find((variant) => Number(variant.id) === variantId) || variants.find((variant) => variant.available);
  }

  function getPackState(selector) {
    if (selector.packState) return selector.packState;

    const defaultOptions = getNativeVariant(selector)?.options || [];
    selector.packState = {
      pairs: Array.from({ length: PACK_MAXIMUM }, () => ({
        options: [...defaultOptions],
        touched: false,
      })),
      submitting: false,
    };

    return selector.packState;
  }

  function setFormQuantity(selector) {
    const form = getProductForm(selector);
    if (!form) return;

    let input = form.querySelector('input[name="quantity"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      form.appendChild(input);
    }

    // Multi-packs are submitted as individual variants, so the native form
    // quantity must remain one to prevent an accidental duplicate fallback.
    input.value = '1';
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function updateShippingProgress(selector, option) {
    const progress = getProductInfo(selector)?.querySelector('[data-shipping-progress]');
    if (!progress || !option) return;

    const packPrice = Number(option.dataset.packPriceValue || 0);
    const threshold = Number(progress.dataset.shippingThreshold || 50);
    const unlocked = packPrice >= threshold;
    const percentage = Math.min((packPrice / threshold) * 100, 100);
    const currency = progress.dataset.shippingCurrency || 'GBP';
    const locale = progress.dataset.shippingLocale || document.documentElement.lang || 'en-GB';
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    const remaining = Math.max(threshold - packPrice, 0);
    const currentPrice = option.dataset.packDisplayPrice || formatter.format(packPrice);
    const thresholdPrice = progress.dataset.shippingThresholdDisplay || formatter.format(threshold);

    const message = progress.querySelector('[data-shipping-message]');
    const current = progress.querySelector('[data-shipping-current]');
    const bar = progress.querySelector('[data-shipping-bar]');
    const meter = progress.querySelector('[role="progressbar"]');

    if (message) message.textContent = unlocked ? 'Free shipping unlocked' : `Spend ${formatter.format(remaining)} more for free shipping`;
    if (current) current.textContent = currentPrice;
    if (bar) bar.style.width = `${percentage}%`;
    if (meter) {
      meter.setAttribute('aria-valuenow', String(Math.min(packPrice, threshold)));
      meter.setAttribute('aria-valuetext', unlocked ? 'Free shipping unlocked' : `${currentPrice} of ${thresholdPrice}`);
    }
  }

  function updateAddToCartLabel(selector, option) {
    const button = getProductInfo(selector)?.querySelector('button[name="add"]');
    const label = button?.querySelector('.cta');
    const price = option?.dataset.packCtaPrice || option?.dataset.packDisplayPrice;

    if (!button || !label || button.disabled || !price) return;
    label.textContent = `Add to cart — ${price}`;

    const floatingPrice = getProductInfo(selector)?.querySelector('[data-floating-cart-price]');
    if (floatingPrice) floatingPrice.textContent = price;
  }

  function variantMatchesOptions(variant, options) {
    return variant.options.length === options.length && variant.options.every((value, index) => value === options[index]);
  }

  function findVariant(selector, options, availableOnly) {
    return getVariants(selector).find((variant) => {
      if (availableOnly && !variant.available) return false;
      return variantMatchesOptions(variant, options);
    });
  }

  function updatePairRow(selector, row, pairIndex) {
    const pair = getPackState(selector).pairs[pairIndex];
    if (!pair) return;

    const pairNumber = row.querySelector('[data-pack-pair-number]');
    const summary = row.querySelector('[data-pack-pair-summary]');
    if (pairNumber) pairNumber.textContent = String(pairIndex + 1);
    if (summary) summary.textContent = pair.options.join(' / ');

    row.querySelectorAll('[data-pack-choice]').forEach((choice) => {
      const optionIndex = Number(choice.dataset.packOptionIndex);
      const value = choice.dataset.packOptionValue;
      const selected = pair.options[optionIndex] === value;
      const candidate = [...pair.options];
      candidate[optionIndex] = value;
      const available = Boolean(findVariant(selector, candidate, true));

      choice.classList.toggle('is-selected', selected);
      choice.setAttribute('aria-pressed', selected ? 'true' : 'false');
      choice.disabled = !available;
    });
  }

  function renderPairPicker(selector, quantity) {
    const panel = selector.querySelector(`[data-pack-selections="${quantity}"]`);
    if (!panel) return;

    const list = panel.querySelector('[data-pack-pair-list]');
    const template = selector.querySelector('[data-pack-pair-template]');
    if (!list || !template) return;

    if (list.children.length !== quantity) {
      list.replaceChildren();
      for (let pairIndex = 0; pairIndex < quantity; pairIndex += 1) {
        const row = template.content.firstElementChild.cloneNode(true);
        row.dataset.packPairIndex = String(pairIndex);
        list.appendChild(row);
      }
    }

    Array.from(list.children).forEach((row, pairIndex) => updatePairRow(selector, row, pairIndex));
  }

  function syncPairPickers(selector) {
    const quantity = getSelectedQuantity(selector);
    const productInfo = getProductInfo(selector);

    selector.querySelectorAll('[data-pack-selections]').forEach((panel) => {
      const selected = Number(panel.dataset.packSelections) === quantity;
      panel.hidden = !selected;
      if (selected) renderPairPicker(selector, quantity);
    });

    selector.querySelectorAll('[data-pack-group]').forEach((group) => {
      const selected = Number(group.dataset.packGroup) === quantity;
      group.classList.toggle('is-selected', selected);
      group.classList.toggle('has-pair-picker', selected && quantity > 1);
    });

    productInfo?.classList.toggle('has-custom-pack-selections', quantity > 1);
  }

  function syncSelectedPack(selector) {
    const option = getSelectedPack(selector);
    setFormQuantity(selector);
    syncPairPickers(selector);
    updateShippingProgress(selector, option);
    updateAddToCartLabel(selector, option);
  }

  function clearFormError(selector) {
    const wrapper = getProductInfo(selector)?.querySelector('.product-form__error-message-wrapper');
    const message = wrapper?.querySelector('.product-form__error-message');
    if (message) message.textContent = '';
    if (wrapper) wrapper.hidden = true;
  }

  function selectPack(selector, selectedOption) {
    selector.querySelectorAll('[data-pack-quantity]').forEach((option) => {
      const selected = option === selectedOption;
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-checked', selected ? 'true' : 'false');
      if (option.hasAttribute('aria-expanded')) option.setAttribute('aria-expanded', selected ? 'true' : 'false');
    });

    clearFormError(selector);
    syncSelectedPack(selector);
  }

  function syncNativeOption(selector, optionIndex, value) {
    const variantSelects = getProductInfo(selector)?.querySelector('variant-selects');
    const optionGroup = variantSelects?.querySelectorAll('.product-form__input')[optionIndex];
    if (!optionGroup) return;

    const radio = Array.from(optionGroup.querySelectorAll('input[type="radio"]')).find((input) => input.value === value);
    if (radio) {
      if (!radio.checked && !radio.disabled) radio.click();
      return;
    }

    const select = optionGroup.querySelector('select');
    if (select && select.value !== value) {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function selectPairOption(selector, choice) {
    if (choice.disabled) return;

    const row = choice.closest('[data-pack-pair]');
    const pairIndex = Number(row?.dataset.packPairIndex);
    const optionIndex = Number(choice.dataset.packOptionIndex);
    const pair = getPackState(selector).pairs[pairIndex];
    if (!pair || Number.isNaN(optionIndex)) return;

    pair.options[optionIndex] = choice.dataset.packOptionValue;
    pair.touched = true;
    updatePairRow(selector, row, pairIndex);
    clearFormError(selector);

    // Pair one remains the page's active Shopify variant so its product image,
    // URL and availability stay in sync with the custom bundle controls.
    if (pairIndex === 0) syncNativeOption(selector, optionIndex, pair.options[optionIndex]);
  }

  function syncStateFromNativeVariant(selector) {
    const variant = getNativeVariant(selector);
    if (!variant) return;

    const state = getPackState(selector);
    state.pairs[0].options = [...variant.options];

    if (getSelectedQuantity(selector) === 1) {
      state.pairs.slice(1).forEach((pair) => {
        if (!pair.touched) pair.options = [...variant.options];
      });
    }

    syncSelectedPack(selector);
  }

  function buildCartItems(selector) {
    const quantity = getSelectedQuantity(selector);
    const pairs = getPackState(selector).pairs.slice(0, quantity);
    const merged = new Map();

    pairs.forEach((pair) => {
      const variant = findVariant(selector, pair.options, true);
      if (!variant) throw new Error(`The ${pair.options.join(' / ')} combination is unavailable.`);

      const id = Number(variant.id);
      merged.set(id, (merged.get(id) || 0) + 1);
    });

    return Array.from(merged, ([id, itemQuantity]) => ({ id, quantity: itemQuantity }));
  }

  function showFormError(selector, text) {
    const wrapper = getProductInfo(selector)?.querySelector('.product-form__error-message-wrapper');
    const message = wrapper?.querySelector('.product-form__error-message');
    if (message) message.textContent = text;
    if (wrapper) wrapper.hidden = false;
  }

  function setSubmitting(selector, submitting) {
    const state = getPackState(selector);
    const button = getProductInfo(selector)?.querySelector('button[name="add"]');
    const spinner = button?.querySelector('.loading__spinner');
    const ajaxForm = getProductForm(selector)?.closest('ajax-cart-product-form');

    state.submitting = submitting;
    button?.classList.toggle('loading', submitting);
    spinner?.classList.toggle('hidden', !submitting);
    if (button) button.setAttribute('aria-disabled', submitting ? 'true' : 'false');
    if (ajaxForm) ajaxForm.toggleAttribute('processing', submitting);
  }

  function getRequestError(requestState) {
    const body = requestState?.responseData?.body;
    if (typeof body?.description === 'string') return body.description;
    if (typeof body?.message === 'string') return body.message;
    if (typeof body?.errors === 'string') return body.errors;
    return 'We could not add this pack to your cart. Please try again.';
  }

  function addWithLiquidAjaxCart(selector, items) {
    const form = getProductForm(selector);
    const initiator = form?.closest('ajax-cart-product-form') || form;

    window.liquidAjaxCart.add(
      { items },
      {
        info: { initiator },
        lastCallback: (requestState) => {
          if (!requestState.responseData?.ok) showFormError(selector, getRequestError(requestState));
          setSubmitting(selector, false);
        },
      }
    );
  }

  function addWithCartFallback(selector, items) {
    const root = window.Shopify?.routes?.root || '/';
    fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items }),
    })
      .then(async (response) => {
        const body = await response.json();
        if (!response.ok) throw new Error(body.description || body.message || 'Cart request failed');
        window.location.assign(window.routes?.cart_url || `${root}cart`);
      })
      .catch((error) => {
        showFormError(selector, error.message || 'We could not add this pack to your cart. Please try again.');
        setSubmitting(selector, false);
      });
  }

  function submitMultiPack(selector) {
    const state = getPackState(selector);
    if (state.submitting) return;

    clearFormError(selector);

    let items;
    try {
      items = buildCartItems(selector);
    } catch (error) {
      showFormError(selector, error.message);
      return;
    }

    setSubmitting(selector, true);

    if (typeof window.liquidAjaxCart?.add === 'function') {
      addWithLiquidAjaxCart(selector, items);
      return;
    }

    let handled = false;
    const handleAjaxCartReady = () => {
      if (handled) return;
      handled = true;
      window.clearTimeout(fallbackTimer);
      addWithLiquidAjaxCart(selector, items);
    };
    const fallbackTimer = window.setTimeout(() => {
      if (handled) return;
      handled = true;
      document.removeEventListener('liquid-ajax-cart:init', handleAjaxCartReady);
      addWithCartFallback(selector, items);
    }, 1500);

    document.addEventListener('liquid-ajax-cart:init', handleAjaxCartReady, { once: true });
  }

  function initPackSelector(selector) {
    if (selector.dataset.initialized === 'true') return;
    selector.dataset.initialized = 'true';

    getPackState(selector);

    selector.addEventListener('click', (event) => {
      const choice = event.target.closest('[data-pack-choice]');
      if (choice) {
        selectPairOption(selector, choice);
        return;
      }

      const option = event.target.closest('[data-pack-quantity]');
      if (option) selectPack(selector, option);
    });

    const productInfo = getProductInfo(selector);
    productInfo?.addEventListener('change', (event) => {
      if (!event.target.matches('input[name="id"]')) return;
      window.setTimeout(() => syncStateFromNativeVariant(selector), 0);
    });

    const form = getProductForm(selector);
    form?.addEventListener(
      'submit',
      (event) => {
        if (getSelectedQuantity(selector) === 1) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        submitMultiPack(selector);
      },
      true
    );

    syncSelectedPack(selector);
  }

  function init() {
    document.querySelectorAll('[data-pack-selector]').forEach(initPackSelector);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', init);
}());
